package com.ragnorak.clanybackoffice.ui.screens

import androidx.lifecycle.ViewModel
import com.clany.backoffice.data.network.response.AdvancedNotificationFilter
import com.clany.backoffice.data.network.response.NotificationChannelType
import com.clany.backoffice.data.network.response.NotificationType
import com.clany.backoffice.data.network.response.PreviewTargetRequest
import com.clany.backoffice.data.network.response.SendEmailRequest
import com.clany.backoffice.data.network.response.SendPushRequest
import com.clany.backoffice.domain.repository.NotificationRepository
import dev.zacsweers.metro.Inject
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch

// ── Channel enum ─────────────────────────────────────────────────────────────

enum class NotificationChannel {
    PUSH, EMAIL
}

// ── Tri-state helper ─────────────────────────────────────────────────────────

enum class TriStateOption {
    ANY, YES, NO;

    fun toNullableBoolean(): Boolean? = when (this) {
        YES -> true
        NO -> false
        ANY -> null
    }
}

// ── UI State ─────────────────────────────────────────────────────────────────

data class NotificationsUiState(
    // Message content
    val channel: NotificationChannel = NotificationChannel.PUSH,
    val notificationType: NotificationType = NotificationType.REENGAGEMENT,
    val title: String = "",
    val body: String = "",
    val dataKeys: List<String> = emptyList(),
    val dataValues: List<String> = emptyList(),

    // ── User profile filters ──────────────────────────────────────────────────
    val selectedUserTypes: Set<String> = emptySet(),
    val selectedUserStatuses: Set<String> = emptySet(),
    val localesRaw: String = "",                        // comma-separated input
    val selectedAuthProviders: Set<String> = emptySet(),
    val appVersionsRaw: String = "",                    // comma-separated
    val isAnonymized: TriStateOption = TriStateOption.ANY,
    val shouldUpdateTyc: TriStateOption = TriStateOption.ANY,
    val hasLinkedMember: TriStateOption = TriStateOption.ANY,
    val createdAfter: String = "",                      // YYYY-MM-DD
    val createdBefore: String = "",
    val lastActiveAfter: String = "",
    val lastActiveBefore: String = "",

    // ── Device / push filters ─────────────────────────────────────────────────
    val selectedPlatforms: Set<String> = emptySet(),
    val hasDeviceTokens: TriStateOption = TriStateOption.ANY,

    // ── Account filters ───────────────────────────────────────────────────────
    val hasAccounts: TriStateOption = TriStateOption.ANY,
    val minAccountCount: String = "",
    val maxAccountCount: String = "",
    val selectedAccountRoles: Set<String> = emptySet(),
    val selectedAccountUserStatuses: Set<String> = emptySet(),

    // ── Subscription filters ──────────────────────────────────────────────────
    val hasActiveSubscription: TriStateOption = TriStateOption.ANY,
    val selectedSubscriptionPlans: Set<String> = emptySet(),
    val selectedSubscriptionStatuses: Set<String> = emptySet(),

    // ── Member filters ────────────────────────────────────────────────────────
    val isMember: TriStateOption = TriStateOption.ANY,
    val minMemberCount: String = "",
    val maxMemberCount: String = "",

    // ── Event filters ─────────────────────────────────────────────────────────
    val hasEvents: TriStateOption = TriStateOption.ANY,
    val minEventCount: String = "",
    val maxEventCount: String = "",
    val selectedEventTypes: Set<String> = emptySet(),

    // ── Invitation filters ────────────────────────────────────────────────────
    val hasSentInvitations: TriStateOption = TriStateOption.ANY,
    val hasReceivedInvitations: TriStateOption = TriStateOption.ANY,
    val selectedInvitationStatuses: Set<String> = emptySet(),
    val minSentInvitations: String = "",
    val maxSentInvitations: String = "",
    val minReceivedInvitations: String = "",
    val maxReceivedInvitations: String = "",

    // ── Specific user IDs ─────────────────────────────────────────────────────
    val includeUserIdsRaw: String = "",                 // one per line
    val excludeUserIdsRaw: String = "",

    // ── UI feedback ───────────────────────────────────────────────────────────
    val isSending: Boolean = false,
    val successMessage: String? = null,
    val errorMessage: String? = null,
    val previewCount: Int? = null,
    val previewUsers: List<String> = emptyList(),
    val isPreviewing: Boolean = false,
    val previewError: String? = null
)

// ── ViewModel ─────────────────────────────────────────────────────────────────

@Inject
class NotificationsViewModel(
    private val notificationRepository: NotificationRepository
) : ViewModel() {

    private val viewModelScope = CoroutineScope(Dispatchers.Main)

    private val _uiState = MutableStateFlow(NotificationsUiState())
    val uiState: StateFlow<NotificationsUiState> = _uiState.asStateFlow()

    // ── Helpers ───────────────────────────────────────────────────────────────

    private fun resetPreview(state: NotificationsUiState): NotificationsUiState =
        state.copy(previewCount = null, previewUsers = emptyList(), previewError = null)

    private fun <T> Set<T>.toggle(item: T): Set<T> =
        if (contains(item)) this - item else this + item

    // ── Message content ───────────────────────────────────────────────────────

    fun updateChannel(channel: NotificationChannel) {
        _uiState.update { resetPreview(it).copy(channel = channel) }
    }

    fun updateNotificationType(type: NotificationType) {
        _uiState.update { it.copy(notificationType = type) }
    }

    fun updateTitle(title: String) {
        _uiState.update { it.copy(title = title) }
    }

    fun updateBody(body: String) {
        _uiState.update { it.copy(body = body) }
    }

    fun addDataField() {
        _uiState.update {
            it.copy(dataKeys = it.dataKeys + "", dataValues = it.dataValues + "")
        }
    }

    fun updateDataKey(index: Int, key: String) {
        _uiState.update { state ->
            val keys = state.dataKeys.toMutableList()
            if (index in keys.indices) keys[index] = key
            state.copy(dataKeys = keys)
        }
    }

    fun updateDataValue(index: Int, value: String) {
        _uiState.update { state ->
            val values = state.dataValues.toMutableList()
            if (index in values.indices) values[index] = value
            state.copy(dataValues = values)
        }
    }

    fun removeDataField(index: Int) {
        _uiState.update { state ->
            val keys = state.dataKeys.toMutableList()
            val values = state.dataValues.toMutableList()
            if (index in keys.indices) {
                keys.removeAt(index)
                values.removeAt(index)
            }
            state.copy(dataKeys = keys, dataValues = values)
        }
    }

    // ── User profile toggles ──────────────────────────────────────────────────

    fun toggleUserType(type: String) {
        _uiState.update { resetPreview(it).copy(selectedUserTypes = it.selectedUserTypes.toggle(type)) }
    }

    fun toggleUserStatus(status: String) {
        _uiState.update { resetPreview(it).copy(selectedUserStatuses = it.selectedUserStatuses.toggle(status)) }
    }

    fun updateLocalesRaw(value: String) {
        _uiState.update { resetPreview(it).copy(localesRaw = value) }
    }

    fun toggleAuthProvider(provider: String) {
        _uiState.update { resetPreview(it).copy(selectedAuthProviders = it.selectedAuthProviders.toggle(provider)) }
    }

    fun updateAppVersionsRaw(value: String) {
        _uiState.update { resetPreview(it).copy(appVersionsRaw = value) }
    }

    fun setIsAnonymized(option: TriStateOption) {
        _uiState.update { resetPreview(it).copy(isAnonymized = option) }
    }

    fun setShouldUpdateTyc(option: TriStateOption) {
        _uiState.update { resetPreview(it).copy(shouldUpdateTyc = option) }
    }

    fun setHasLinkedMember(option: TriStateOption) {
        _uiState.update { resetPreview(it).copy(hasLinkedMember = option) }
    }

    fun updateCreatedAfter(value: String) {
        _uiState.update { resetPreview(it).copy(createdAfter = value) }
    }

    fun updateCreatedBefore(value: String) {
        _uiState.update { resetPreview(it).copy(createdBefore = value) }
    }

    fun updateLastActiveAfter(value: String) {
        _uiState.update { resetPreview(it).copy(lastActiveAfter = value) }
    }

    fun updateLastActiveBefore(value: String) {
        _uiState.update { resetPreview(it).copy(lastActiveBefore = value) }
    }

    // ── Device toggles ────────────────────────────────────────────────────────

    fun togglePlatform(platform: String) {
        _uiState.update { resetPreview(it).copy(selectedPlatforms = it.selectedPlatforms.toggle(platform)) }
    }

    fun setHasDeviceTokens(option: TriStateOption) {
        _uiState.update { resetPreview(it).copy(hasDeviceTokens = option) }
    }

    // ── Account toggles ───────────────────────────────────────────────────────

    fun setHasAccounts(option: TriStateOption) {
        _uiState.update { resetPreview(it).copy(hasAccounts = option) }
    }

    fun updateMinAccountCount(value: String) {
        _uiState.update { resetPreview(it).copy(minAccountCount = value) }
    }

    fun updateMaxAccountCount(value: String) {
        _uiState.update { resetPreview(it).copy(maxAccountCount = value) }
    }

    fun toggleAccountRole(role: String) {
        _uiState.update { resetPreview(it).copy(selectedAccountRoles = it.selectedAccountRoles.toggle(role)) }
    }

    fun toggleAccountUserStatus(status: String) {
        _uiState.update { resetPreview(it).copy(selectedAccountUserStatuses = it.selectedAccountUserStatuses.toggle(status)) }
    }

    // ── Subscription toggles ──────────────────────────────────────────────────

    fun setHasActiveSubscription(option: TriStateOption) {
        _uiState.update { resetPreview(it).copy(hasActiveSubscription = option) }
    }

    fun toggleSubscriptionPlan(plan: String) {
        _uiState.update { resetPreview(it).copy(selectedSubscriptionPlans = it.selectedSubscriptionPlans.toggle(plan)) }
    }

    fun toggleSubscriptionStatus(status: String) {
        _uiState.update { resetPreview(it).copy(selectedSubscriptionStatuses = it.selectedSubscriptionStatuses.toggle(status)) }
    }

    // ── Member toggles ────────────────────────────────────────────────────────

    fun setIsMember(option: TriStateOption) {
        _uiState.update { resetPreview(it).copy(isMember = option) }
    }

    fun updateMinMemberCount(v: String) {
        _uiState.update { resetPreview(it).copy(minMemberCount = v) }
    }

    fun updateMaxMemberCount(v: String) {
        _uiState.update { resetPreview(it).copy(maxMemberCount = v) }
    }

    // ── Event toggles ─────────────────────────────────────────────────────────

    fun setHasEvents(v: TriStateOption) {
        _uiState.update { resetPreview(it).copy(hasEvents = v) }
    }

    fun updateMinEventCount(v: String) {
        _uiState.update { resetPreview(it).copy(minEventCount = v) }
    }

    fun updateMaxEventCount(v: String) {
        _uiState.update { resetPreview(it).copy(maxEventCount = v) }
    }

    fun toggleEventType(type: String) {
        _uiState.update { resetPreview(it).copy(selectedEventTypes = it.selectedEventTypes.toggle(type)) }
    }

    // ── Invitation toggles ────────────────────────────────────────────────────

    fun setHasSentInvitations(option: TriStateOption) {
        _uiState.update { resetPreview(it).copy(hasSentInvitations = option) }
    }

    fun setHasReceivedInvitations(option: TriStateOption) {
        _uiState.update { resetPreview(it).copy(hasReceivedInvitations = option) }
    }

    fun toggleInvitationStatus(status: String) {
        _uiState.update { resetPreview(it).copy(selectedInvitationStatuses = it.selectedInvitationStatuses.toggle(status)) }
    }

    fun updateMinSentInvitations(value: String) {
        _uiState.update { resetPreview(it).copy(minSentInvitations = value) }
    }

    fun updateMaxSentInvitations(value: String) {
        _uiState.update { resetPreview(it).copy(maxSentInvitations = value) }
    }

    fun updateMinReceivedInvitations(v: String) {
        _uiState.update { resetPreview(it).copy(minReceivedInvitations = v) }
    }

    fun updateMaxReceivedInvitations(v: String) {
        _uiState.update { resetPreview(it).copy(maxReceivedInvitations = v) }
    }

    // ── Specific user IDs ─────────────────────────────────────────────────────

    fun updateIncludeUserIdsRaw(value: String) {
        _uiState.update { resetPreview(it).copy(includeUserIdsRaw = value) }
    }

    fun updateExcludeUserIdsRaw(value: String) {
        _uiState.update { resetPreview(it).copy(excludeUserIdsRaw = value) }
    }

    // ── Misc ──────────────────────────────────────────────────────────────────

    fun dismissMessage() {
        _uiState.update { it.copy(successMessage = null, errorMessage = null) }
    }

    // ── Date helper ───────────────────────────────────────────────────────────

    fun epochFromDateString(s: String): Long? {
        if (s.isBlank()) return null
        return try {
            val parts = s.trim().split("-")
            if (parts.size != 3) return null
            val year = parts[0].toInt()
            val month = parts[1].toInt()
            val day = parts[2].toInt()
            // Simple epoch calculation (UTC midnight) — multiplatform, no java.util.Date
            val daysToYear = daysFromEpochToYear(year)
            val daysToMonth = daysInMonthsBefore(month, year)
            (daysToYear + daysToMonth + day - 1) * 86400_000L
        } catch (e: Exception) {
            null
        }
    }

    private fun isLeapYear(year: Int) = (year % 4 == 0 && year % 100 != 0) || year % 400 == 0

    private fun daysFromEpochToYear(year: Int): Long {
        var days = 0L
        for (y in 1970 until year) days += if (isLeapYear(y)) 366L else 365L
        return days
    }

    private fun daysInMonthsBefore(month: Int, year: Int): Int {
        val days = intArrayOf(31, if (isLeapYear(year)) 29 else 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31)
        return days.take(month - 1).sum()
    }

    // ── Build filter ──────────────────────────────────────────────────────────

    private fun buildFilter(): AdvancedNotificationFilter {
        val state = _uiState.value

        val includeIds = state.includeUserIdsRaw
            .lines()
            .map { it.trim() }
            .filter { it.isNotBlank() }
            .takeIf { it.isNotEmpty() }

        val excludeIds = state.excludeUserIdsRaw
            .lines()
            .map { it.trim() }
            .filter { it.isNotBlank() }
            .takeIf { it.isNotEmpty() }

        val locales = state.localesRaw
            .split(",")
            .map { it.trim() }
            .filter { it.isNotBlank() }
            .takeIf { it.isNotEmpty() }

        val appVersions = state.appVersionsRaw
            .splitToSequence(",")
            .map { it.trim() }
            .filter { it.isNotBlank() }
            .toList()
            .takeIf { it.isNotEmpty() }

        return AdvancedNotificationFilter(
            includeUserIds = includeIds,
            excludeUserIds = excludeIds,
            userTypes = state.selectedUserTypes.toList().takeIf { it.isNotEmpty() },
            userStatuses = state.selectedUserStatuses.toList().takeIf { it.isNotEmpty() },
            locales = locales,
            authProviders = state.selectedAuthProviders.toList().takeIf { it.isNotEmpty() },
            appVersions = appVersions,
            isAnonymized = state.isAnonymized.toNullableBoolean(),
            shouldUpdateTyc = state.shouldUpdateTyc.toNullableBoolean(),
            hasLinkedMember = state.hasLinkedMember.toNullableBoolean(),
            createdAfter = epochFromDateString(state.createdAfter),
            createdBefore = epochFromDateString(state.createdBefore),
            lastActiveAfter = epochFromDateString(state.lastActiveAfter),
            lastActiveBefore = epochFromDateString(state.lastActiveBefore),
            platforms = state.selectedPlatforms.toList().takeIf { it.isNotEmpty() },
            hasDeviceTokens = state.hasDeviceTokens.toNullableBoolean(),
            hasAccounts = state.hasAccounts.toNullableBoolean(),
            minAccountCount = state.minAccountCount.toIntOrNull(),
            maxAccountCount = state.maxAccountCount.toIntOrNull(),
            accountRoles = state.selectedAccountRoles.toList().takeIf { it.isNotEmpty() },
            accountUserStatuses = state.selectedAccountUserStatuses.toList().takeIf { it.isNotEmpty() },
            hasActiveSubscription = state.hasActiveSubscription.toNullableBoolean(),
            subscriptionPlans = state.selectedSubscriptionPlans.toList().takeIf { it.isNotEmpty() },
            subscriptionStatuses = state.selectedSubscriptionStatuses.toList().takeIf { it.isNotEmpty() },
            isMember = state.isMember.toNullableBoolean(),
            minMemberCount = state.minMemberCount.toIntOrNull(),
            maxMemberCount = state.maxMemberCount.toIntOrNull(),
            hasEvents = state.hasEvents.toNullableBoolean(),
            minEventCount = state.minEventCount.toIntOrNull(),
            maxEventCount = state.maxEventCount.toIntOrNull(),
            eventTypes = state.selectedEventTypes.toList().takeIf { it.isNotEmpty() },
            hasSentInvitations = state.hasSentInvitations.toNullableBoolean(),
            hasReceivedInvitations = state.hasReceivedInvitations.toNullableBoolean(),
            invitationStatuses = state.selectedInvitationStatuses.toList().takeIf { it.isNotEmpty() },
            minSentInvitations = state.minSentInvitations.toIntOrNull(),
            maxSentInvitations = state.maxSentInvitations.toIntOrNull(),
            minReceivedInvitations = state.minReceivedInvitations.toIntOrNull(),
            maxReceivedInvitations = state.maxReceivedInvitations.toIntOrNull()
        )
    }

    // ── Preview ───────────────────────────────────────────────────────────────

    fun calculatePreview() {
        val state = _uiState.value
        _uiState.update {
            it.copy(isPreviewing = true, previewCount = null, previewUsers = emptyList(), previewError = null)
        }
        viewModelScope.launch {
            val channelType = if (state.channel == NotificationChannel.PUSH) {
                NotificationChannelType.PUSH
            } else {
                NotificationChannelType.EMAIL
            }
            val request = PreviewTargetRequest(
                filter = buildFilter(),
                channel = channelType
            )
            notificationRepository.previewTarget(request).fold(
                onSuccess = { response ->
                    _uiState.update {
                        it.copy(
                            isPreviewing = false,
                            previewCount = response.count,
                            previewUsers = response.sampleUsers
                        )
                    }
                },
                onFailure = { error ->
                    _uiState.update {
                        it.copy(
                            isPreviewing = false,
                            previewError = "Error al calcular destinatarios: ${error.message}"
                        )
                    }
                }
            )
        }
    }

    // ── Send ──────────────────────────────────────────────────────────────────

    fun sendNotification() {
        val state = _uiState.value
        if (state.title.isBlank() || state.body.isBlank()) {
            _uiState.update { it.copy(errorMessage = "El título y el cuerpo son obligatorios.") }
            return
        }

        _uiState.update { it.copy(isSending = true, errorMessage = null, successMessage = null) }

        viewModelScope.launch {
            val filter = buildFilter()

            if (state.channel == NotificationChannel.PUSH) {
                val dataMap = state.dataKeys.zip(state.dataValues).toMap().filterKeys { it.isNotBlank() }
                val request = SendPushRequest(
                    title = state.title,
                    body = state.body,
                    data = dataMap.takeIf { it.isNotEmpty() },
                    filter = filter,
                    environment = "prod",
                    notificationType = state.notificationType
                )
                notificationRepository.sendNotification(request).fold(
                    onSuccess = { response ->
                        _uiState.update {
                            it.copy(
                                isSending = false,
                                successMessage = "Enviado correctamente a ${response.successCount} dispositivos. Fallidos: ${response.failureCount}"
                            )
                        }
                    },
                    onFailure = { error ->
                        _uiState.update {
                            it.copy(
                                isSending = false,
                                errorMessage = "Error al enviar: ${error.message}"
                            )
                        }
                    }
                )
            } else {
                val request = SendEmailRequest(
                    subject = state.title,
                    bodyHtml = state.body,
                    filter = filter,
                    notificationType = state.notificationType
                )
                notificationRepository.sendEmail(request).fold(
                    onSuccess = {
                        _uiState.update {
                            it.copy(
                                isSending = false,
                                successMessage = "Emails programados para su envío correctamente."
                            )
                        }
                    },
                    onFailure = { error ->
                        _uiState.update {
                            it.copy(
                                isSending = false,
                                errorMessage = "Error al enviar emails: ${error.message}"
                            )
                        }
                    }
                )
            }
        }
    }
}
